import { cn } from '../../../lib/utils';
import type { ExternalIntelAvailabilityReason } from '../../../api/intelClient';

interface UnavailablePlaceholderProps {
  reason: ExternalIntelAvailabilityReason;
  region?: string | null;
  className?: string;
}

/**
 * Four failure-mode placeholders rendered when a per-region row's
 * `availability_reason` is anything other than `available`. Sprint-1
 * scaffolds the surface; sprint-2 (FR-2.3) wires the full failure-UX
 * semantics — retry affordance, log-correlation breadcrumbs, etc.
 *
 * Backend `availability_reason` vocabulary → placeholder mapping
 * (post-bug-190 brief §What changed row 5):
 *
 *   available             → not rendered (live row instead)
 *   invalid_provenance    → "schema-blocked" placeholder
 *   stale                 → "stale" placeholder
 *   schema_version_unknown→ "schema-blocked" placeholder
 *   upstream_down         → "network-error" placeholder
 *
 * Five reasons collapse to four placeholder visuals because
 * `invalid_provenance` and `schema_version_unknown` are both
 * schema-integrity failures from the operator's vantage.
 */
export function UnavailablePlaceholder({
  reason,
  region,
  className,
}: UnavailablePlaceholderProps) {
  const variant = mapReasonToVariant(reason);
  const copy = VARIANT_COPY[variant];

  return (
    <div
      data-testid={`external-intel-placeholder-${variant}`}
      data-reason={reason}
      role="status"
      className={cn(
        'flex items-center gap-2 px-2 py-1.5 text-[10px] font-mono',
        'border border-dashed border-[var(--e-border-secondary)]/50',
        'rounded-sm bg-[var(--e-bg-sunken)]/40 text-[var(--e-text-muted)]',
        className,
      )}
    >
      {region && (
        <span className="uppercase tracking-wider text-[var(--e-text-secondary)]">
          {region}
        </span>
      )}
      <span className="uppercase tracking-wider text-[var(--e-text-muted)]/70">
        {copy.label}
      </span>
      <span className="ml-auto italic text-[var(--e-text-muted)]/60">
        {copy.detail}
      </span>
    </div>
  );
}

type PlaceholderVariant = 'stale' | 'schema-blocked' | 'network-error' | 'dead-letter';

function mapReasonToVariant(
  reason: ExternalIntelAvailabilityReason,
): PlaceholderVariant {
  switch (reason) {
    case 'stale':
      return 'stale';
    case 'invalid_provenance':
    case 'schema_version_unknown':
      return 'schema-blocked';
    case 'upstream_down':
      return 'network-error';
    case 'available':
      // Defensive — caller should not invoke for 'available' rows. Fall
      // through to dead-letter so the surface is honest if it does.
      return 'dead-letter';
  }
}

const VARIANT_COPY: Record<PlaceholderVariant, { label: string; detail: string }> = {
  stale: { label: 'stale', detail: 'data past freshness window' },
  'schema-blocked': { label: 'schema blocked', detail: 'provenance integrity failed' },
  'network-error': { label: 'network error', detail: 'upstream unreachable' },
  'dead-letter': { label: 'dead letter', detail: 'rejected by validator' },
};
